// assets/gravur-vorschau.js
// Rendert die sichtbare Gravur-Vorschau beim Absenden in ein PNG und haengt es
// als Datei-Property an die Warenkorbzeile. Der reine Textwert verliert in der
// Bestellansicht die Zeilenaufteilung — das Bild zeigt sie exakt so, wie der
// Kunde sie gesetzt hat, inklusive Position und Groesse auf der Box.
// Reine Funktionen sind DOM-frei und werden mit node --test geprueft.
import { registerSectionLifecycle, replaceInputFile } from "./motiv-zuschnitt.js";

// Kantenlaenge des Exports. Gross genug fuer die Werkstatt, klein genug,
// dass der Upload den Checkout nicht ausbremst.
export const VORSCHAU_BREITE = 1000;
// Notbremse: lieber ohne Bild bestellen als gar nicht.
export const RENDER_TIMEOUT_MS = 2500;

// Quellrechteck fuer object-fit: cover — dieselbe Beschneidung, die der
// Browser in der Vorschau zeigt, sonst sitzt der Text spaeter versetzt.
export function coverRect(sourceWidth, sourceHeight, targetWidth, targetHeight) {
  const sw = Number(sourceWidth);
  const sh = Number(sourceHeight);
  const tw = Number(targetWidth);
  const th = Number(targetHeight);
  if (![sw, sh, tw, th].every(value => Number.isFinite(value) && value > 0)) {
    return { x: 0, y: 0, width: Math.max(1, sw || 1), height: Math.max(1, sh || 1) };
  }
  const scale = Math.max(tw / sw, th / sh);
  const width = tw / scale;
  const height = th / scale;
  return { x: (sw - width) / 2, y: (sh - height) / 2, width, height };
}

export function vorschauZeilen(text) {
  return String(text ?? "").split("\n");
}

// Mittiger y-Versatz je Zeile, damit der Block wie im Overlay zentriert sitzt.
export function zeilenVersatz(index, count, lineHeight) {
  const total = Number(count) || 0;
  const height = Number(lineHeight) || 0;
  return (Number(index) - (total - 1) / 2) * height;
}

// JPEG statt PNG: die Vorschau ist ein Foto mit Text drauf. Als PNG waeren das
// schnell ueber 1 MB, die beim Absenden mit hochgeladen werden muessten.
export const VORSCHAU_TYP = "image/jpeg";
export const VORSCHAU_QUALITAET = 0.92;

export function vorschauDateiname(handle) {
  const base = String(handle || "gravur").replace(/[^a-z0-9-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
  return `${base || "gravur"}-vorschau.jpg`;
}

function ladeBildMitCors(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    // Ohne crossOrigin verunreinigt das CDN-Bild die Canvas und toBlob wirft.
    // Shopifys CDN liefert access-control-allow-origin: *.
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Vorschaubild nicht ladbar"));
    image.src = src;
  });
}

function mitTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Render-Timeout")), ms);
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

export function initGravurVorschau(root) {
  if (root.dataset.gravurVorschauReady === "true") return;
  const fileInput = root.querySelector("[data-gravur-vorschau-datei]");
  const form = root.querySelector("form[action*='/cart/add']");
  if (!fileInput || !form) return;
  root.dataset.gravurVorschauReady = "true";

  let abgeschickt = false;

  // Die sichtbare Vorschau: Desktop die grosse Buehne, mobil die Inline-Karte.
  // getClientRects() unterscheidet zuverlaessig, weil beide per CSS getauscht
  // werden — ein Media-Query-Nachbau im JS wuerde frueher oder spaeter driften.
  function sichtbareVorschau() {
    const kandidaten = Array.from(root.querySelectorAll("[data-box-gravur-overlay]"));
    return kandidaten
      .map(overlay => ({ overlay, container: overlay.parentElement }))
      .find(({ overlay, container }) =>
        !overlay.hidden &&
        overlay.getClientRects().length > 0 &&
        container?.clientWidth > 0 &&
        container?.clientHeight > 0
      ) ?? null;
  }

  async function rendern() {
    const treffer = sichtbareVorschau();
    if (!treffer) return false;
    const { overlay, container } = treffer;
    const text = overlay.textContent ?? "";
    if (!text.trim()) return false;

    const bild = container.querySelector("img");
    if (!bild?.currentSrc && !bild?.src) return false;

    const rahmen = container.getBoundingClientRect();
    const massstab = VORSCHAU_BREITE / rahmen.width;
    const canvas = document.createElement("canvas");
    canvas.width = VORSCHAU_BREITE;
    canvas.height = Math.max(1, Math.round(rahmen.height * massstab));
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;

    // JPEG kennt keine Transparenz — ohne Grundierung wuerden unbedeckte
    // Bereiche schwarz. Das Cover-Rechteck deckt zwar alles ab, aber der
    // weisse Grund kostet nichts und schuetzt vor Rundungsluecken am Rand.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const quelle = await ladeBildMitCors(bild.currentSrc || bild.src);
    const ausschnitt = coverRect(quelle.naturalWidth, quelle.naturalHeight, canvas.width, canvas.height);
    ctx.drawImage(
      quelle,
      ausschnitt.x, ausschnitt.y, ausschnitt.width, ausschnitt.height,
      0, 0, canvas.width, canvas.height
    );

    // Schrift, Farbe und Position direkt aus dem gerenderten Overlay ableiten,
    // statt die Layout-Rechnung des Konfigurators nachzubauen.
    const stil = getComputedStyle(overlay);
    const overlayRahmen = overlay.getBoundingClientRect();
    const schriftgroesse = parseFloat(stil.fontSize) * massstab;
    const zeilenhoehe = (parseFloat(stil.lineHeight) || parseFloat(stil.fontSize) * 1.15) * massstab;
    ctx.font = `${stil.fontStyle} ${stil.fontWeight} ${schriftgroesse}px ${stil.fontFamily}`;
    ctx.fillStyle = stil.color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const mitteX = (overlayRahmen.left - rahmen.left + overlayRahmen.width / 2) * massstab;
    const mitteY = (overlayRahmen.top - rahmen.top + overlayRahmen.height / 2) * massstab;
    const zeilen = vorschauZeilen(text);
    zeilen.forEach((zeile, index) => {
      ctx.fillText(zeile, mitteX, mitteY + zeilenVersatz(index, zeilen.length, zeilenhoehe));
    });

    const blob = await new Promise(resolve => canvas.toBlob(resolve, VORSCHAU_TYP, VORSCHAU_QUALITAET));
    if (!blob) return false;
    const datei = new File([blob], vorschauDateiname(root.dataset.produktHandle), { type: VORSCHAU_TYP });
    return replaceInputFile(fileInput, datei);
  }

  function absendenErzwingen(submitter) {
    if (typeof form.requestSubmit === "function") form.requestSubmit(submitter ?? undefined);
    else form.submit();
  }

  const beimAbsenden = (event) => {
    if (abgeschickt) return;
    const treffer = sichtbareVorschau();
    // Ohne Gravur gibt es nichts zu rendern — Formular normal durchlassen.
    if (!treffer || !(treffer.overlay.textContent ?? "").trim()) return;
    event.preventDefault();
    abgeschickt = true;
    const submitter = event.submitter ?? form.querySelector("[data-add-to-cart]");
    if (submitter) submitter.setAttribute("aria-busy", "true");
    mitTimeout(rendern(), RENDER_TIMEOUT_MS)
      .catch(() => false)
      .finally(() => absendenErzwingen(submitter));
  };

  form.addEventListener("submit", beimAbsenden);

  return function cleanupGravurVorschau() {
    form.removeEventListener("submit", beimAbsenden);
    delete root.dataset.gravurVorschauReady;
  };
}

if (typeof document !== "undefined") {
  registerSectionLifecycle({
    documentLike: document,
    selector: "[data-product-configurator]",
    initialize: initGravurVorschau
  });
}
